/** Normalized catalog attachment row from get-all-operators-with-version `files[]`. */
export type CatalogFileEntry = {
  id: string;
  sas: string;
  /** Includes leading dot when known (e.g. `.pdf`). */
  ext: string;
  fileName: string;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function catalogFileId(row: Record<string, unknown>): string | null {
  const id = row.id ?? row.Id ?? row.zohoId ?? row.ZohoId;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

function catalogFileExt(row: Record<string, unknown>): string {
  const ext = row.ext ?? row.Ext ?? row.fileExt ?? row.FileExt;
  if (typeof ext !== "string" || !ext.trim()) return "";
  const t = ext.trim();
  return t.startsWith(".") ? t : `.${t}`;
}

function catalogFileSas(row: Record<string, unknown>): string | null {
  const sas = row.sas ?? row.SAS;
  return typeof sas === "string" && sas.trim() ? sas.trim() : null;
}

export function normalizeCatalogFileRow(item: unknown): CatalogFileEntry | null {
  const r = asRecord(item);
  if (!r) return null;
  const id = catalogFileId(r);
  const sas = catalogFileSas(r);
  if (!id || !sas) return null;
  const ext = catalogFileExt(r);
  return { id, sas, ext, fileName: `${id}${ext}` };
}

function normalizeLookupExt(ext: string | undefined): string {
  if (!ext?.trim()) return "";
  const t = ext.trim();
  return t.startsWith(".") ? t : `.${t}`;
}

/** True when a payload/catalog id matches a catalog row (Zoho id with or without extension). */
export function catalogFileIdsMatch(
  want: string,
  entry: CatalogFileEntry,
  fileExt?: string,
): boolean {
  const w = want.trim();
  if (!w) return false;
  if (w === entry.id) return true;
  const ext = normalizeLookupExt(fileExt) || entry.ext;
  if (ext) {
    const composite = `${entry.id}${ext}`;
    if (w === composite) return true;
    if (w.toLowerCase().endsWith(ext.toLowerCase())) {
      const base = w.slice(0, -ext.length);
      if (base === entry.id) return true;
    }
  }
  return false;
}

/** Find a catalog `files[]` row by Zoho/file id for one operator. */
/** Resolve one operator row from get-all-operators-with-version (tolerant id field). */
export function findCatalogOperatorRow(
  catalog: ReadonlyArray<Record<string, unknown>> | null | undefined,
  operatorId: string,
): Record<string, unknown> | null {
  const want = operatorId.trim();
  if (!want || !catalog?.length) return null;
  for (const op of catalog) {
    const id =
      typeof op.operatorId === "string" && op.operatorId.trim()
        ? op.operatorId.trim()
        : typeof op.id === "string" && op.id.trim()
          ? op.id.trim()
          : typeof op.OperatorId === "string" && op.OperatorId.trim()
            ? op.OperatorId.trim()
            : "";
    if (id && id === want) return op;
  }
  return null;
}

export function findCatalogFileEntry(
  operatorRow: Record<string, unknown> | null | undefined,
  fileId: string,
  fileExt?: string,
): CatalogFileEntry | null {
  if (!operatorRow || !fileId.trim()) return null;
  const rawFiles = operatorRow.files ?? operatorRow.Files;
  if (!Array.isArray(rawFiles)) return null;
  const want = fileId.trim();
  for (const item of rawFiles) {
    const entry = normalizeCatalogFileRow(item);
    if (entry && catalogFileIdsMatch(want, entry, fileExt)) return entry;
  }
  return null;
}

/**
 * Zoho file ids from one operator's catalog `files[]` — same set mobile stores via
 * `setManifestFileIds` after `get-all-operators-with-version` (jurisdiction-filtered server-side).
 */
export function extractManifestFileIds(
  catalogOperatorRow: Record<string, unknown> | null | undefined,
): Set<string> | undefined {
  if (!catalogOperatorRow) return undefined;
  const rawFiles = catalogOperatorRow.files ?? catalogOperatorRow.Files;
  if (!Array.isArray(rawFiles)) return undefined;
  const ids = new Set<string>();
  for (const item of rawFiles) {
    const entry = normalizeCatalogFileRow(item);
    if (entry) ids.add(entry.id);
  }
  return ids;
}

/**
 * Mirrors mobile `getFilesInManifest`: when manifest ids are known, keep only payload rows
 * whose `id` is in the catalog manifest; when unknown, return all rows (legacy fallback).
 */
export function filterPayloadFileRowsInManifest<T extends { id: string }>(
  rows: readonly T[],
  manifestFileIds: Set<string> | undefined,
): T[] {
  if (!manifestFileIds) return [...rows];
  return rows.filter((row) => row.id && manifestFileIds.has(row.id));
}

export function buildCatalogFileIndex(
  catalog: ReadonlyArray<Record<string, unknown>>,
): Map<string, Map<string, CatalogFileEntry>> {
  const byOperator = new Map<string, Map<string, CatalogFileEntry>>();
  for (const op of catalog) {
    const operatorId =
      typeof op.operatorId === "string" && op.operatorId.trim()
        ? op.operatorId.trim()
        : typeof op.id === "string" && op.id.trim()
          ? op.id.trim()
          : "";
    if (!operatorId) continue;
    const rawFiles = op.files ?? op.Files;
    if (!Array.isArray(rawFiles)) continue;
    let files = byOperator.get(operatorId);
    if (!files) {
      files = new Map();
      byOperator.set(operatorId, files);
    }
    for (const item of rawFiles) {
      const entry = normalizeCatalogFileRow(item);
      if (entry) files.set(entry.id, entry);
    }
  }
  return byOperator;
}
